import { useMantineTheme } from '@mantine/core';
import type {
	SecodaAIProposalOut,
	SuggestDescriptionArg,
} from '@repo/api-codegen';
import type { IBaseModel } from '@repo/common/models/baseModel';
import { useCallback, useMemo, type MouseEvent } from 'react';
import { useSecodaEntityList } from '../../../../api';
import { SecodaEntityIcon } from '../../../SecodaEntity';
import { SuggestionItemTable } from './SuggestionItemTable';

type DescriptionSuggestionWithBaseModel = SuggestDescriptionArg & IBaseModel;

export interface DescriptionSuggestionTableProps {
	suggestions: {
		proposal: SecodaAIProposalOut;
		data: SuggestDescriptionArg;
	}[];
	onRowClick: (
		row: SuggestDescriptionArg & IBaseModel,
		index: number,
		event: MouseEvent
	) => void;
}

export function DescriptionSuggestionTable({
	suggestions,
	onRowClick,
}: DescriptionSuggestionTableProps) {
	const theme = useMantineTheme();
	const { data: entities } = useSecodaEntityList({
		page_size: suggestions.length + 10,
		filters: {
			id__in: suggestions.map((suggestion) => suggestion.data.entity_id),
		},
	});

	const getEntityById = useCallback(
		(entityId: string) =>
			entities?.results.find((entity) => entity.id === entityId),
		[entities]
	);

	const records = useMemo(
		() =>
			suggestions.map((suggestion) => ({
				...suggestion.data,
				id: suggestion.proposal.id,
				created_at: suggestion.proposal.created_at,
				updated_at: suggestion.proposal.updated_at,
			})),
		[suggestions]
	);

	return (
		<SuggestionItemTable<DescriptionSuggestionWithBaseModel>
			items={records}
			iconRenderer={(item) => {
				const entity = getEntityById(item.entity_id);
				return <SecodaEntityIcon entity={entity} size={20} />;
			}}
			titleSelector={(item) => {
				const entity = getEntityById(item.entity_id);
				return entity?.title || item.entity_id;
			}}
			subTextSelector={() => 'Update description'}
			onItemClick={onRowClick}
		/>
	);
}
